import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { rideAPI, deliveryAPI } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { toast } from 'sonner';
import {
  Car,
  Truck,
  MapPin,
  Navigation,
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  Play,
  User,
  TrendingUp
} from 'lucide-react';

const JobCard = ({ job, type, onAccept, onUpdateStatus }) => {
  const isRide = type === 'ride';
  const Icon = isRide ? Car : Truck;
  
  const statusFlow = isRide 
    ? ['accepted', 'arrived', 'in_progress', 'completed']
    : ['accepted', 'picked_up', 'in_transit', 'delivered'];
  
  const currentIndex = statusFlow.indexOf(job.status);
  const nextStatus = statusFlow[currentIndex + 1];
  
  const statusLabels = {
    pending: 'Pendiente',
    accepted: isRide ? 'En camino al pasajero' : 'En camino a recoger',
    arrived: 'Llegaste al origen',
    in_progress: 'Viaje en curso',
    picked_up: 'Paquete recogido',
    in_transit: 'En tránsito',
    completed: 'Completado',
    delivered: 'Entregado',
  };

  const nextStatusLabels = {
    accepted: isRide ? 'Iniciar viaje al pasajero' : 'Ir a recoger',
    arrived: 'Llegué al origen',
    in_progress: 'Iniciar viaje',
    picked_up: 'Recogí el paquete',
    in_transit: 'En camino a entregar',
    completed: 'Completar viaje',
    delivered: 'Marcar como entregado',
  };

  return (
    <Card className="card-elevated overflow-hidden" data-testid={`job-${job.id}`}>
      <div className={`h-1 ${job.status === 'pending' ? 'bg-yellow-500' : 'bg-[#00BFA6]'}`} />
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#00BFA6]/10 flex items-center justify-center">
              <Icon className="w-5 h-5 text-[#00BFA6]" />
            </div>
            <div>
              <p className="text-slate-800 font-semibold">
                {isRide ? 'Viaje' : job.delivery_type?.replace('_', ' ')}
              </p>
              <p className="text-slate-500 text-sm">{statusLabels[job.status]}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xl font-bold text-[#00BFA6]">${job.estimated_price}</p>
            <p className="text-slate-400 text-xs">
              {isRide ? `${job.distance_km} km` : `${job.package_weight_kg || 1} kg`}
            </p>
          </div>
        </div>

        {/* Locations */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#00BFA6] shrink-0" />
            <p className="text-slate-600 text-sm truncate">
              {job.pickup_location?.address || 'Origen'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Navigation className="w-4 h-4 text-[#FFD166] shrink-0" />
            <p className="text-slate-600 text-sm truncate">
              {(isRide ? job.destination : job.delivery_location)?.address || 'Destino'}
            </p>
          </div>
        </div>

        {/* Actions */}
        {job.status === 'pending' ? (
          <Button
            onClick={() => onAccept(job.id)}
            className="w-full h-12 btn-primary"
            data-testid={`accept-job-${job.id}`}
          >
            <CheckCircle2 className="w-5 h-5 mr-2" />
            Aceptar
          </Button>
        ) : nextStatus ? (
          <Button
            onClick={() => onUpdateStatus(job.id, nextStatus)}
            className="w-full h-12 bg-[#00BFA6] text-white hover:bg-[#00887A] rounded-xl font-semibold"
            data-testid={`update-status-${job.id}`}
          >
            <Play className="w-5 h-5 mr-2" />
            {nextStatusLabels[nextStatus]}
          </Button>
        ) : (
          <div className="flex items-center justify-center gap-2 py-3 text-[#00BFA6]">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-semibold">Completado</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function DriverDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('available');
  const [availableRides, setAvailableRides] = useState([]);
  const [availableDeliveries, setAvailableDeliveries] = useState([]);
  const [myRides, setMyRides] = useState([]);
  const [myDeliveries, setMyDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ rides: 0, deliveries: 0, earnings: 0 });

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [availRides, availDeliveries, userRides, userDeliveries] = await Promise.all([
        rideAPI.getAvailable().catch(() => ({ data: [] })),
        deliveryAPI.getAvailable().catch(() => ({ data: [] })),
        rideAPI.getAll().catch(() => ({ data: [] })),
        deliveryAPI.getAll().catch(() => ({ data: [] })),
      ]);

      setAvailableRides(availRides.data);
      setAvailableDeliveries(availDeliveries.data);
      setMyRides(userRides.data.filter(r => r.driver_id === user?.id));
      setMyDeliveries(userDeliveries.data.filter(d => d.driver_id === user?.id));

      // Calculate stats
      const completedRides = userRides.data.filter(r => r.status === 'completed' && r.driver_id === user?.id);
      const completedDeliveries = userDeliveries.data.filter(d => d.status === 'delivered' && d.driver_id === user?.id);
      const totalEarnings = 
        completedRides.reduce((sum, r) => sum + (r.final_price || r.estimated_price), 0) +
        completedDeliveries.reduce((sum, d) => sum + (d.final_price || d.estimated_price), 0);

      setStats({
        rides: completedRides.length,
        deliveries: completedDeliveries.length,
        earnings: totalEarnings,
      });
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRide = async (rideId) => {
    try {
      await rideAPI.accept(rideId);
      toast.success('¡Viaje aceptado!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aceptar');
    }
  };

  const handleAcceptDelivery = async (deliveryId) => {
    try {
      await deliveryAPI.accept(deliveryId);
      toast.success('¡Envío aceptado!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aceptar');
    }
  };

  const handleUpdateRideStatus = async (rideId, status) => {
    try {
      await rideAPI.updateStatus(rideId, status);
      toast.success('Estado actualizado');
      loadData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleUpdateDeliveryStatus = async (deliveryId, status) => {
    try {
      await deliveryAPI.updateStatus(deliveryId, status);
      toast.success('Estado actualizado');
      loadData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const activeJobs = [...myRides, ...myDeliveries].filter(j => 
    !['completed', 'delivered', 'cancelled'].includes(j.status)
  );

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 font-['Outfit']">Panel del Conductor</h1>
            <p className="text-slate-500 mt-1">Gestiona tus viajes y entregas</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="card-elevated p-4 text-center">
            <Car className="w-8 h-8 text-[#00BFA6] mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-800">{stats.rides}</p>
            <p className="text-slate-500 text-sm">Viajes</p>
          </Card>
          <Card className="card-elevated p-4 text-center">
            <Truck className="w-8 h-8 text-[#FFD166] mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-800">{stats.deliveries}</p>
            <p className="text-slate-500 text-sm">Entregas</p>
          </Card>
          <Card className="card-elevated p-4 text-center">
            <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-800">${stats.earnings.toFixed(2)}</p>
            <p className="text-slate-500 text-sm">Ganancias</p>
          </Card>
        </div>

        {/* Active Jobs Alert */}
        {activeJobs.length > 0 && (
          <Card className="card-elevated border-[#00BFA6]/30 bg-[#00BFA6]/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#00BFA6]/20 flex items-center justify-center animate-pulse">
                  <Play className="w-5 h-5 text-[#00BFA6]" />
                </div>
                <div>
                  <p className="text-slate-800 font-semibold">Tienes {activeJobs.length} trabajo(s) activo(s)</p>
                  <p className="text-slate-500 text-sm">Revisa la pestaña "Mis trabajos"</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-2 bg-slate-100">
            <TabsTrigger value="available" className="data-[state=active]:bg-[#00BFA6] data-[state=active]:text-white">
              Disponibles ({availableRides.length + availableDeliveries.length})
            </TabsTrigger>
            <TabsTrigger value="my-jobs" className="data-[state=active]:bg-[#00BFA6] data-[state=active]:text-white">
              Mis trabajos ({myRides.length + myDeliveries.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="available" className="mt-6 space-y-4">
            {loading ? (
              <div className="space-y-4">
                {[1, 2].map((i) => <div key={i} className="h-48 skeleton rounded-2xl" />)}
              </div>
            ) : availableRides.length + availableDeliveries.length > 0 ? (
              <>
                {availableRides.map((ride) => (
                  <JobCard
                    key={ride.id}
                    job={ride}
                    type="ride"
                    onAccept={handleAcceptRide}
                    onUpdateStatus={handleUpdateRideStatus}
                  />
                ))}
                {availableDeliveries.map((delivery) => (
                  <JobCard
                    key={delivery.id}
                    job={delivery}
                    type="delivery"
                    onAccept={handleAcceptDelivery}
                    onUpdateStatus={handleUpdateDeliveryStatus}
                  />
                ))}
              </>
            ) : (
              <div className="text-center py-12">
                <Car className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No hay trabajos disponibles</p>
                <p className="text-slate-400 text-sm mt-1">Los nuevos pedidos aparecerán aquí</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="my-jobs" className="mt-6 space-y-4">
            {myRides.length + myDeliveries.length > 0 ? (
              <>
                {myRides.map((ride) => (
                  <JobCard
                    key={ride.id}
                    job={ride}
                    type="ride"
                    onAccept={handleAcceptRide}
                    onUpdateStatus={handleUpdateRideStatus}
                  />
                ))}
                {myDeliveries.map((delivery) => (
                  <JobCard
                    key={delivery.id}
                    job={delivery}
                    type="delivery"
                    onAccept={handleAcceptDelivery}
                    onUpdateStatus={handleUpdateDeliveryStatus}
                  />
                ))}
              </>
            ) : (
              <div className="text-center py-12">
                <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No tienes trabajos asignados</p>
                <p className="text-slate-400 text-sm mt-1">Acepta trabajos de la pestaña "Disponibles"</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
